import env from '../../env.js';

const LICENSE_RENEWAL_PRESETS = {
  daily: [1, 'day'],
  weekly: [7, 'day'],
  biweekly: [14, 'day'],
  monthly: [1, 'month'],
  quarterly: [3, 'month'],
  semiannual: [6, 'month'],
  annual: [1, 'year'],
  biennial: [2, 'year'],
};

// The System pages: products, licenses, orders, returns,
// reloads and user management.
// Merged onto the web-dashboard prototype (see dashboard.js);
// `this` is the component instance.
export default {

    async loadProducts() {
      try {
        if (await this.fetchList('products', `${this.apiUrl('admin/products')}`)) {
          this.productsLoaded = true;
        }
      } catch (e) {}
    },

    openProductDialog(product) {
      if (product) {
        this.productForm = { id: product.id, name: product.name, description: product.description || '', license_product: product.license_product || '', is_active: !!product.is_active };
        this.productEditMode = true;
        this.productDialogTitle = 'Edit Product';
      } else {
        this.productForm = { id: '', name: '', description: '', license_product: '', is_active: true };
        this.productEditMode = false;
        this.productDialogTitle = 'Add Product';
      }
      this.update();
      this.querySelector('.product-dialog').showModal();
    },

    closeProductDialog() {
      this.querySelector('.product-dialog').close();
    },

    async saveProduct() {
      for (const input of this.querySelectorAll('.product-dialog input, .product-dialog textarea')) {
        if (!input.reportValidity()) return;
      }
      if (!this.productEditMode) {
        this.closeProductDialog();
        this.openLicenseDefinitionDialog(null, true);
        return;
      }
      const url = this.productEditMode
        ? `${this.apiUrl('admin/product?id=' + encodeURIComponent(this.productForm.id))}`
        : `${this.apiUrl('admin/products')}`;
      const method = this.productEditMode ? 'PUT' : 'POST';
      try {
        const response = await this.apiFetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: this.productForm.name,
            description: this.productForm.description,
            license_product: this.productForm.license_product,
            is_active: this.productForm.is_active,
          }),
        });
        const msg = await response.json();
        if (response.ok) {
          this.closeProductDialog();
          this.showToast(msg);
          await this.loadProducts();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    async deleteProduct(productID) {
      const confirmed = await this.showConfirm({
        title: 'Delete Product',
        message: 'Are you sure you want to deactivate this product? It will no longer appear in the catalog.',
        action: 'Delete',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${this.apiUrl('admin/product?id=' + encodeURIComponent(productID))}`, { method: 'DELETE' });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadProducts();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    async loadLicenseDefinitions() {
      try {
        if (await this.fetchList('licenseDefinitions', this.apiUrl('admin/license-definitions'))) {
          this.licenseDefinitionsLoaded = true;
        }
      } catch (e) {}
    },

    async loadLicenseDefinitionOptions() {
      const session = sessionStorage.getItem('session');
      const seq = this._licenseDefinitionOptionsSeq = (this._licenseDefinitionOptionsSeq || 0) + 1;
      this.licenseDefinitionOptionsLoading = true;
      this.update();
      try {
        const definitions = [];
        for (let page = 1; ; page++) {
          const response = await this.apiFetch(this.apiUrl('admin/license-definitions?size=100&page=' + page));
          if (!response.ok) throw new Error('Could not load license definitions');
          const data = await response.json();
          const rows = data.rows || [];
          definitions.push(...rows);
          if (rows.length === 0 || definitions.length >= data.total) break;
        }
        if (seq === this._licenseDefinitionOptionsSeq && session === sessionStorage.getItem('session')) this.licenseDefinitionOptions = definitions;
      } catch (e) {
        if (seq === this._licenseDefinitionOptionsSeq && session === sessionStorage.getItem('session') && !e.sessionExpired) this.showToast(e.message, 'danger');
      } finally {
        if (seq === this._licenseDefinitionOptionsSeq && session === sessionStorage.getItem('session')) {
          this.licenseDefinitionOptionsLoading = false;
          this.update();
        }
      }
    },

    async loadLicenseProductOptions() {
      const session = sessionStorage.getItem('session');
      const seq = this._licenseProductOptionsSeq = (this._licenseProductOptionsSeq || 0) + 1;
      this.licenseProductOptionsLoading = true;
      this.update();
      try {
        const products = [];
        for (let page = 1; ; page++) {
          const response = await this.apiFetch(this.apiUrl('admin/products?size=100&page=' + page));
          if (!response.ok) throw new Error('Could not load products');
          const data = await response.json();
          const rows = data.rows || [];
          products.push(...rows);
          if (rows.length === 0 || products.length >= data.total) break;
        }
        if (seq === this._licenseProductOptionsSeq && session === sessionStorage.getItem('session')) this.licenseProductOptions = products;
      } catch (e) {
        if (seq === this._licenseProductOptionsSeq && session === sessionStorage.getItem('session') && !e.sessionExpired) this.showToast(e.message, 'danger');
      } finally {
        if (seq === this._licenseProductOptionsSeq && session === sessionStorage.getItem('session')) {
          this.licenseProductOptionsLoading = false;
          this.update();
        }
      }
    },

    licenseProductChoices(selectedID) {
      return this.licenseProductOptions.filter((product) => (product.is_active && product.license_product) || product.id === selectedID);
    },

    licenseDefinitionChoices(selectedID) {
      return this.licenseDefinitionOptions.filter((definition) => definition.is_active || definition.id === selectedID);
    },

    selectedLicenseDefinition(id) {
      return this.licenseDefinitionOptions.find((definition) => definition.id === id);
    },

    licenseDefinitionLabel(definition) {
      return definition.name + ' · ' + (definition.product_name || definition.product) + (definition.is_active ? '' : ' (inactive)');
    },

    licenseDefinitionRenewalTerms(definition) {
      return definition.renew_period > 0 && definition.renew_price > 0 ? this.formatAmount(definition.renew_price) + ' / ' + this.licenseRenewalPeriod(definition) : 'Disabled';
    },

    licenseDefinitionSummary(id) {
      const definition = this.selectedLicenseDefinition(id);
      if (!definition) return '';
      if (definition.model === 'lifetime') return 'Lifetime license · No expiry or renewal';
      const initial = this.licenseRenewalPeriod({ renew_period: definition.initial_period, renew_unit: definition.initial_unit });
      return 'Initial term: ' + initial + (definition.renew_period > 0 ? ' · Renewal: ' + this.licenseDefinitionRenewalTerms(definition) : ' · Renewal disabled');
    },

    openLicenseDefinitionDialog(definition, creatingProduct = false) {
      if (this.licenseDefinitionSaving) return;
      this.creatingProduct = creatingProduct;
      this.licenseDefinitionForm = definition ? {
        ...definition,
        initial_choice: this.licenseRenewChoice({ renew_period: definition.initial_period, renew_unit: definition.initial_unit }),
        renew_choice: this.licenseRenewChoice(definition),
        is_active: !!definition.is_active,
      } : { id: '', name: '', product_id: '', price: '', model: 'lifetime', initial_period: 1, initial_unit: 'year', initial_choice: 'annual', renew_period: 0, renew_unit: 'day', renew_choice: 'disabled', renew_price: 0, max_devices: 1, is_active: true };
      this.update();
      this.querySelector('.license-definition-dialog').showModal();
      if (!creatingProduct) this.loadLicenseProductOptions();
    },

    closeLicenseDefinitionDialog() {
      if (this.licenseDefinitionSaving) return;
      this.querySelector('.license-definition-dialog').close();
      if (this.creatingProduct) this.querySelector('.product-dialog').showModal();
      this.creatingProduct = false;
    },

    cancelLicenseDefinition(event) {
      event.preventDefault();
      this.closeLicenseDefinitionDialog();
    },

    setDefinitionModel(model) {
      this.licenseDefinitionForm.model = model;
      if (model === 'term' && !(this.licenseDefinitionForm.initial_period > 0)) this.setDefinitionPeriod('initial', 'annual');
      this.update();
    },

    setDefinitionPeriod(kind, choice) {
      const form = this.licenseDefinitionForm;
      const period = kind + '_period';
      const unit = kind + '_unit';
      form[kind + '_choice'] = choice;
      if (choice === 'disabled') {
        form[period] = 0;
        form[unit] = 'day';
        if (kind === 'renew') form.renew_price = 0;
      } else if (choice === 'custom') {
        form[period] = form[unit] === 'day' && form[period] > 0 ? form[period] : 30;
        form[unit] = 'day';
      } else if (LICENSE_RENEWAL_PRESETS[choice]) {
        [form[period], form[unit]] = LICENSE_RENEWAL_PRESETS[choice];
      }
      this.update();
    },

    async saveLicenseDefinition() {
      if (this.licenseDefinitionSaving) return;
      for (const input of this.querySelectorAll('.license-definition-dialog input, .license-definition-dialog select')) {
        if (!input.reportValidity()) return;
      }
      this.licenseDefinitionSaving = true;
      this.update();
      try {
        const form = this.licenseDefinitionForm;
        const lifetime = form.model === 'lifetime';
        const body = {
          name: form.name.trim(), product_id: form.product_id, price: Number(form.price), model: form.model,
          initial_period: lifetime ? 0 : Number(form.initial_period), initial_unit: lifetime ? 'day' : form.initial_unit,
          renew_period: lifetime ? 0 : Number(form.renew_period), renew_unit: lifetime ? 'day' : form.renew_unit,
          renew_price: lifetime ? 0 : Number(form.renew_price), max_devices: Number(form.max_devices) || 1, is_active: form.is_active,
        };
        const creatingProduct = this.creatingProduct;
        const response = await this.apiFetch(this.apiUrl(creatingProduct ? 'admin/products' : form.id ? 'admin/license-definition?id=' + encodeURIComponent(form.id) : 'admin/license-definitions'), {
          method: form.id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(creatingProduct ? { ...this.productForm, license_definitions: [body] } : body),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data === 'string' ? data : data.message || 'Could not save license definition');
        this.querySelector('.license-definition-dialog').close();
        this.creatingProduct = false;
        this.showToast(creatingProduct ? 'Product and license definition created' : 'License definition saved');
        if (creatingProduct) await this.loadProducts();
        await this.loadLicenseDefinitions();
      } catch (e) {
        if (!e.sessionExpired) this.showToast(e.message, 'danger');
      } finally {
        this.licenseDefinitionSaving = false;
        this.update();
      }
    },

    async deactivateLicenseDefinition(definition) {
      const confirmed = await this.showConfirm({
        title: 'Deactivate License Definition',
        message: 'Stop issuing new licenses from ' + definition.name + '? Existing licenses keep their dates and renewal terms.',
        action: 'Deactivate', danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(this.apiUrl('admin/license-definition?id=' + encodeURIComponent(definition.id)), { method: 'DELETE' });
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data === 'string' ? data : data.message || 'Could not deactivate license definition');
        this.showToast('License definition deactivated');
        await this.loadLicenseDefinitions();
      } catch (e) {
        if (!e.sessionExpired) this.showToast(e.message, 'danger');
      }
    },

    // ---- Promo codes: what a buyer types at checkout on the product sites ----

    async loadPromoCodes() {
      try {
        if (await this.fetchList('promoCodes', this.apiUrl('admin/promo-codes'))) {
          this.promoCodesLoaded = true;
        }
      } catch (e) {}
    },

    // What a code is worth, in the table's words.
    promoDiscountWords(promo) {
      const value = Number(promo.value);
      if (promo.kind === 'percent') return value + '% off';
      if (promo.kind === 'fixed_price') return this.formatAmount(value) + ' a license';
      if (promo.kind === 'amount_off') return this.formatAmount(value) + ' off each license';
      return promo.kind;
    },

    promoAppWords(promo) {
      return promo.product || 'Any app';
    },

    promoUsesWords(promo) {
      return promo.max_uses > 0 ? promo.uses + ' / ' + promo.max_uses : String(promo.uses);
    },

    // When the code can be used: from a day, until a day, both, or always.
    promoValidityWords(promo) {
      const from = promo.starts ? this.formatDate(promo.starts) : '';
      const until = promo.expires ? this.formatDate(promo.expires) : '';
      if (from && until) return from + ' to ' + until;
      if (from) return 'From ' + from;
      if (until) return 'Until ' + until;
      return 'Always';
    },

    // The apps the store sells keys for, one entry each, for the dialog's
    // Applies To — off the products, which name their app.
    promoAppChoices(selected) {
      const apps = [];
      for (const product of this.licenseProductOptions) {
        if (product.license_product && !apps.includes(product.license_product)) apps.push(product.license_product);
      }
      if (selected && !apps.includes(selected)) apps.push(selected);
      return apps.sort();
    },

    // The value field's label follows the kind of discount.
    promoValueLabel() {
      const kind = this.promoCodeForm.kind;
      return kind === 'percent' ? 'Percent off' : kind === 'fixed_price' ? 'Price per license (USD)' : 'Amount off each license (USD)';
    },

    openPromoCodeDialog(promo) {
      if (this.promoCodeSaving) return;
      this.promoCodeForm = promo ? {
        id: promo.id, code: promo.code, kind: promo.kind, value: promo.value, product: promo.product || '', max_uses: promo.max_uses || 0,
        starts: promo.starts ? String(promo.starts).slice(0, 10) : '', expires: promo.expires ? String(promo.expires).slice(0, 10) : '',
        notes: promo.notes || '', is_active: !!promo.is_active, uses: promo.uses || 0,
      } : { id: '', code: '', kind: 'percent', value: '', product: '', max_uses: 0, starts: '', expires: '', notes: '', is_active: true, uses: 0 };
      this.update();
      this.querySelector('.promo-code-dialog').showModal();
      this.loadLicenseProductOptions();
    },

    closePromoCodeDialog() {
      if (this.promoCodeSaving) return;
      this.querySelector('.promo-code-dialog').close();
    },

    cancelPromoCode(event) {
      event.preventDefault();
      this.closePromoCodeDialog();
    },

    // A code nobody would guess: eight characters from the alphabet license
    // keys use (no 0/O or 1/I to confuse), each drawn evenly.
    generatePromoCode() {
      const alphabet = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
      const code = [];
      while (code.length < 8) {
        const [byte] = crypto.getRandomValues(new Uint8Array(1));
        if (byte < 248) code.push(alphabet[byte % alphabet.length]); // 248 = 8 × 31: the largest multiple of the alphabet under 256
      }
      this.promoCodeForm.code = code.join('');
      this.update();
    },

    async savePromoCode() {
      if (this.promoCodeSaving) return;
      for (const input of this.querySelectorAll('.promo-code-dialog input, .promo-code-dialog select')) {
        if (!input.reportValidity()) return;
      }
      this.promoCodeSaving = true;
      this.update();
      try {
        const form = this.promoCodeForm;
        const body = {
          code: form.code.trim(), kind: form.kind, value: Number(form.value), product: form.product, max_uses: Number(form.max_uses) || 0,
          starts: form.starts, expires: form.expires, notes: form.notes, is_active: form.is_active,
        };
        const response = await this.apiFetch(this.apiUrl(form.id ? 'admin/promo-code?id=' + encodeURIComponent(form.id) : 'admin/promo-codes'), {
          method: form.id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data === 'string' ? data : data.message || 'Could not save promo code');
        this.querySelector('.promo-code-dialog').close();
        this.showToast('Promo code saved');
        await this.loadPromoCodes();
      } catch (e) {
        if (!e.sessionExpired) this.showToast(e.message, 'danger');
      } finally {
        this.promoCodeSaving = false;
        this.update();
      }
    },

    async deactivatePromoCode(promo) {
      const confirmed = await this.showConfirm({
        title: 'Deactivate Promo Code',
        message: 'Stop taking ' + promo.code + ' at checkout? Orders that used it keep their price.',
        action: 'Deactivate', danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(this.apiUrl('admin/promo-code?id=' + encodeURIComponent(promo.id)), { method: 'DELETE' });
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data === 'string' ? data : data.message || 'Could not deactivate promo code');
        this.showToast('Promo code deactivated');
        await this.loadPromoCodes();
      } catch (e) {
        if (!e.sessionExpired) this.showToast(e.message, 'danger');
      }
    },

    async loadLicenses() {
      this.tableState('licenses').status ??= 'unrevoked';
      try {
        if (await this.fetchList('licenses', `${this.apiUrl('admin/licenses')}`)) {
          this.licensesLoaded = true;
        }
      } catch (e) {}
    },

    // Licenses status filter: 'unrevoked' (default — hides revoked keys),
    // 'all', or one exact status ('active' | 'available' | 'revoked').
    setLicenseStatus(status) {
      const st = this.tableState('licenses');
      if (st.status === status) return;
      st.status = status;
      st.page = 1;
      this._refetch('licenses');
      this._syncUrl();
    },

    // Licenses product filter: one product, spelled the way keys are issued,
    // or '' for every product. It combines with the status filter.
    setLicenseProduct(product) {
      const st = this.tableState('licenses');
      product = (product || '').trim().toLowerCase();
      if ((st.product || '') === product) return;
      st.product = product;
      st.page = 1;
      this._refetch('licenses');
      this._syncUrl();
    },

    licenseProductFilter() {
      return this.tableState('licenses').product || '';
    },

    // The products the filter offers: every product that has keys, read from
    // the keys themselves (GET admin/license-products) — plus whatever the
    // address asked for, so the select can show a filter that is running
    // before the list arrives, or for a product that has no keys left.
    licenseFilterProducts() {
      const current = this.licenseProductFilter();
      const products = this.licenseProducts.slice();
      if (current && !products.includes(current)) products.push(current);
      return products;
    },

    async loadLicenseProducts() {
      const session = sessionStorage.getItem('session');
      const seq = this._licenseProductsSeq = (this._licenseProductsSeq || 0) + 1;
      try {
        const response = await this.apiFetch(this.apiUrl('admin/license-products'));
        if (!response.ok) return;
        const products = await response.json();
        if (seq === this._licenseProductsSeq && session === sessionStorage.getItem('session') && Array.isArray(products)) {
          this.licenseProducts = products.filter((p) => typeof p === 'string');
        }
      } catch (e) {
        // The list keeps whatever it had: a filter with fewer choices is no
        // reason to toast over the table it stands beside.
      }
    },

    licensesEmptyTitle() {
      const { status, product } = this.tableState('licenses');
      const byStatus = status && status !== 'all' && status !== 'unrevoked' ? status + ' ' : '';
      if (product) return 'No ' + byStatus + product + ' licenses';
      if (byStatus) return 'No ' + byStatus + 'licenses';
      return 'No licenses yet';
    },

    licensesEmptyDescription() {
      const { status, product } = this.tableState('licenses');
      const byStatus = status && status !== 'all' && status !== 'unrevoked';
      if (product && byStatus) return 'No licenses match these filters. Adjust them to see others.';
      if (product) return 'No licenses match this product filter. Adjust the filter to see other products.';
      if (byStatus) return 'No licenses have this status. Adjust the filter to see others.';
      return 'Keys are issued automatically when a license product is purchased, or generate them here.';
    },

    // Verify history dialog: the latest verify attempts recorded for one key
    // (the server caps a page at 100; the footer notes when more exist).
    // The History dialog: one key's verify activity, newest first, paged by
    // the shared table machinery like every list on a page — its foot is the
    // same <web-pager>, which finds this component as its host since the
    // dialog sits in our DOM. Opening starts over on page one.
    async openVerifyHistory(lic) {
      this.verifyHistoryKey = lic.license_key;
      this.verifyHistoryLicenseID = lic.id;
      this.verifyHistory = [];
      this._serverTotals.verifyHistory = 0;
      this.tableState('verifyHistory').page = 1;
      this.verifyHistoryLoaded = false;
      this.update();
      this.querySelector('.verify-history-dialog').showModal();
      await this.loadVerifyHistory();
    },

    async loadVerifyHistory() {
      try {
        await this.fetchList('verifyHistory', `${this.apiUrl('admin/license/verifications')}?license_id=${encodeURIComponent(this.verifyHistoryLicenseID)}`);
      } catch (e) {
      } finally {
        this.verifyHistoryLoaded = true;
        this.update();
      }
    },

    closeVerifyHistory() {
      this.querySelector('.verify-history-dialog').close();
    },

    licenseDateInput(value) {
      if (!value) return '';
      return new Date(value).toISOString().slice(0, 10);
    },

    licenseRenewChoice(lic) {
      if (!(lic.renew_period > 0)) return 'disabled';
      const unit = lic.renew_unit || 'day';
      const preset = Object.entries(LICENSE_RENEWAL_PRESETS).find(([, [period, presetUnit]]) => period === lic.renew_period && presetUnit === unit);
      // Old custom day periods retain their exact count. Non-preset calendar
      // periods set through the API also stay intact when editing other terms.
      return preset ? preset[0] : unit === 'day' ? 'custom' : 'saved';
    },

    setLicenseRenewChoice(choice) {
      const form = this.licenseForm;
      form.renew_choice = choice;
      if (choice === 'disabled') {
        form.renew_period = 0;
        form.renew_unit = 'day';
        form.renew_price = 0;
      } else if (choice === 'custom') {
        form.renew_period = form.renew_unit === 'day' && form.renew_period > 0 ? form.renew_period : 30;
        form.renew_unit = 'day';
      } else if (LICENSE_RENEWAL_PRESETS[choice]) {
        [form.renew_period, form.renew_unit] = LICENSE_RENEWAL_PRESETS[choice];
      }
      this.update();
    },

    openLicenseDialog(lic) {
      if (this.licenseGenLoading) return;
      this.licenseEditMode = !!lic;
      this.licenseForm = lic ? {
        license_key: lic.license_key,
        // Preserve the server value separately from the editable date. A
        // renewal while this dialog is open must not lose its extension.
        expected_end_date: lic.end_date || null,
        start_date: this.licenseDateInput(lic.start_date),
        end_date: this.licenseDateInput(lic.end_date),
        renew_period: lic.renew_period || 0,
        renew_unit: lic.renew_unit || 'day',
        renew_choice: this.licenseRenewChoice(lic),
        renew_price: lic.renew_price || 0,
        max_devices: lic.max_devices || 1,
      } : { count: 1, definition_id: '', product: 'ampere', email: '', name: '', notes: '', start_date: '', end_date: '', renew_period: 0, renew_unit: 'day', renew_choice: 'disabled', renew_price: 0, max_devices: 1 };
      this.update();
      this.querySelector('.license-dialog').showModal();
      if (!lic) this.loadLicenseDefinitionOptions();
    },

    closeLicenseDialog() {
      this.querySelector('.license-dialog').close();
    },

    licenseFormTerms() {
      const form = this.licenseForm;
      const period = Number(form.renew_period);
      const unit = form.renew_unit || 'day';
      const price = Number(form.renew_price);
      const maxPeriod = { day: 36500, month: 1200, year: 100 }[unit];
      if (!maxPeriod || !Number.isInteger(period) || period < 0 || period > maxPeriod) throw new Error('Enter a valid renewal period');
      if (!Number.isFinite(price) || price < 0 || price > 9999999999.99 || Math.abs(price - Math.round(price * 100) / 100) > 0.0000001) throw new Error('Renewal price must be a nonnegative USD amount with at most two decimal places');
      if ((period > 0) !== (price > 0)) throw new Error('Set a renewal price greater than 0, or select Disabled');
      const start = form.start_date ? new Date(form.start_date + 'T00:00:00Z') : null;
      const end = form.end_date ? new Date(form.end_date + 'T00:00:00Z') : null;
      if ((start && isNaN(start)) || (end && isNaN(end))) throw new Error('Enter valid license dates');
      if (start && end && start >= end) throw new Error('End date must be after the start date');
      if (period > 0 && !end) throw new Error('An end date is required to enable renewal');
      const devices = Number(form.max_devices);
      if (!Number.isInteger(devices) || devices < 1 || devices > 100) throw new Error('Devices per key must be a whole number from 1 to 100');
      return { start_date: start ? start.toISOString() : null, end_date: end ? end.toISOString() : null, renew_period: period, renew_unit: unit, renew_price: price, max_devices: devices };
    },

    async generateLicenses() {
      if (this.licenseGenLoading) return;
      for (const input of this.querySelectorAll('.license-dialog input')) {
        if (!input.reportValidity()) return;
      }
      this.licenseGenLoading = true;
      this.update();
      try {
        const definitionID = !this.licenseEditMode && this.licenseForm.definition_id;
        const terms = definitionID ? { start_date: this.licenseForm.start_date ? new Date(this.licenseForm.start_date + 'T00:00:00Z').toISOString() : null } : this.licenseFormTerms();
        const edit = this.licenseEditMode;
        const response = await this.apiFetch(this.apiUrl(edit ? 'admin/license' : 'admin/licenses'), {
          method: edit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(edit ? { license_key: this.licenseForm.license_key, expected_end_date: this.licenseForm.expected_end_date, ...terms } : {
            count: parseInt(this.licenseForm.count, 10) || 1,
            product: this.licenseForm.product,
            definition_id: definitionID || null,
            email: this.licenseForm.email,
            name: this.licenseForm.name,
            notes: this.licenseForm.notes,
            ...terms,
          }),
        });
        const data = await response.json();
        if (response.ok) {
          this.closeLicenseDialog();
          this.showToast(edit ? 'License terms saved' : data.length + ' key' + (data.length === 1 ? '' : 's') + ' generated');
          // A first key for a product is a new choice in the product filter.
          await Promise.all([this.loadLicenses(), this.loadLicenseProducts()]);
        } else {
          this.showToast(data, 'danger');
          if (edit && response.status === 409) {
            this.closeLicenseDialog();
            await this.loadLicenses();
          }
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      } finally {
        this.licenseGenLoading = false;
        this.update();
      }
    },

    async revokeLicense(key) {
      const confirmed = await this.showConfirm({
        title: 'Revoke License',
        message: 'Revoke ' + key + '? Registration and verification will fail until it is released.',
        action: 'Revoke',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${this.apiUrl('admin/license/revoke')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ license_key: key }),
        });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadLicenses();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    // source: 'admin' releases any key from the store Licenses page; 'user'
    // releases one of the signed-in user's own keys from My Licenses. With a
    // device (a row of the Devices dialog) only that device is taken off the
    // key and the key keeps its others; without one, every device goes.
    async releaseLicense(key, source, device) {
      const isAdmin = source === 'admin';
      const which = device ? (device.device_serial || device.device_hint) : '';
      const confirmed = await this.showConfirm({
        title: device ? 'Release Device' : 'Release License',
        message: device
          ? 'Take device ' + which + ' off ' + key + '? The app there goes back to unregistered at its next check, and another device can register.'
          : isAdmin
            ? 'Return ' + key + ' to available and drop every device it holds? The owner email stays on the key.'
            : 'Release ' + key + ' from all its devices? You can register it on other devices afterwards.',
        action: 'Release',
        danger: false,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${this.apiUrl(isAdmin ? 'admin/license/release' : 'license/release')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(device ? { license_key: key, device_id: device.id } : { license_key: key }),
        });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          // An open Devices dialog follows through listLoaded.
          await (isAdmin ? this.loadLicenses() : this.loadMyLicenses());
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    async loadOrders() {
      try {
        if (await this.fetchList('orders', `${this.apiUrl('admin/orders')}`)) {
          this.ordersLoaded = true;
        }
      } catch (e) {}
    },

    async loadReturnRequests() {
      try {
        if (await this.fetchList('returnRequests', `${this.apiUrl('admin/return-requests')}`)) {
          this.returnRequestsLoaded = true;
        }
      } catch (e) {}
    },

    async approveReturn(requestID) {
      const confirmed = await this.showConfirm({
        title: 'Approve Return',
        message: 'Approve this return request and send the refund?',
        action: 'Approve',
        choices: [
          { value: 'gateway', label: 'Refund to the original payment method' },
          { value: 'balance', label: 'Refund to account balance' },
        ],
        choice: 'gateway',
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${this.apiUrl('admin/return-request/approve?id=' + encodeURIComponent(requestID) + '&to=' + encodeURIComponent(this.confirmChoice))}`, { method: 'POST' });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadReturnRequests();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    async denyReturn(requestID) {
      const confirmed = await this.showConfirm({
        title: 'Deny Return',
        message: 'Deny this return request?',
        action: 'Deny',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${this.apiUrl('admin/return-request/deny?id=' + encodeURIComponent(requestID))}`, { method: 'POST' });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadReturnRequests();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    async loadAdminReloads() {
      try {
        if (await this.fetchList('adminReloads', `${this.apiUrl('admin/reloads')}`)) {
          this.adminReloadsLoaded = true;
        }
      } catch (e) {}
    },

    async loadAdminTransactions() {
      try {
        if (await this.fetchList('adminTransactions', `${this.apiUrl('admin/transactions')}`)) {
          this.adminTransactionsLoaded = true;
        }
      } catch (e) {}
    },

    // The ledger's type filter (all/reload/subscription/payment/refund),
    // mirroring the security log's role filter: reset to page 1, refetch,
    // and keep the URL restorable.
    setTransactionsType(type) {
      const st = this.tableState('adminTransactions');
      if (st.type === type) return;
      st.type = type;
      st.page = 1;
      this._refetch('adminTransactions');
      this._syncUrl();
    },

    async loadAdminSubscriptions() {
      try {
        if (await this.fetchList('adminSubscriptions', `${this.apiUrl('admin/subscriptions')}`)) {
          this.adminSubscriptionsLoaded = true;
        }
      } catch (e) {}
    },

    // Admin-side stop of a user's recurring reload: same wording and
    // gateway path as the owner's own cancel, naming whose it is.
    async adminCancelSubscription(sub) {
      const cycle = { week: 'weekly', month: 'monthly', year: 'yearly' }[sub.billing_interval] || 'recurring';
      const confirmed = await this.showConfirm({
        title: 'Cancel Recurring Reload',
        message: 'Stop ' + sub.user_email + "'s " + this.formatAmount(sub.amount) + ' ' + cycle + ' reload? Balance already credited stays theirs.',
        action: 'Stop Reloads',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${this.apiUrl('admin/subscription/cancel')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: sub.id }),
        });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadAdminSubscriptions();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    transactionType(transaction) {
      return transaction.purpose === 'license_renewal' && transaction.type === 'payment' ? 'License renewal' : this.formatStatus(transaction.type);
    },

    transactionStatus(transaction) {
      return this.reloadRefundState(transaction) || transaction.status || 'completed';
    },

    transactionPaymentSplit(transaction) {
      if (transaction.purpose !== 'license_renewal' || transaction.type !== 'payment') return '';
      const balance = Number(transaction.balance_amount || 0);
      const gateway = Number(transaction.gateway_amount || 0);
      return gateway > 0 ? this.formatAmount(balance) + ' balance + ' + this.formatAmount(gateway) + ' ' + this.formatGateway(transaction.gateway) : this.formatAmount(balance) + ' balance';
    },

    openLicenseRefund(transaction) {
      if (this.refundLicenseLoading || (!transaction.can_refund && transaction.status !== 'refunding')) return;
      this.refundLicenseTx = transaction;
      this.refundLicenseTo = transaction.status === 'refunding' ? transaction.refund_to || 'gateway' : Number(transaction.gateway_amount || 0) > 0 ? 'gateway' : 'balance';
      this.update();
      this.querySelector('.license-refund-dialog').showModal();
    },

    closeLicenseRefund() {
      if (!this.refundLicenseLoading) this.querySelector('.license-refund-dialog').close();
    },

    licenseRefundDestination() {
      const transaction = this.refundLicenseTx;
      return this.formatAmount(transaction.gateway_amount || 0) + ' to ' + this.formatGateway(transaction.gateway) + ' and ' + this.formatAmount(transaction.balance_amount || 0) + ' to account balance';
    },

    async confirmLicenseRefund() {
      if (this.refundLicenseLoading) return;
      this.refundLicenseLoading = true;
      this.update();
      try {
        const response = await this.apiFetch(this.apiUrl('admin/refund-license-renewal'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transaction_id: this.refundLicenseTx.id, refund_to: this.refundLicenseTo }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data === 'string' ? data : data.message || 'Could not refund license renewal');
        this.showToast(data.message);
        this.querySelector('.license-refund-dialog').close();
      } catch (e) {
        if (!e.sessionExpired) this.showToast(e.message, 'danger');
      } finally {
        await this.loadAdminTransactions();
        const current = this.adminTransactions.find((transaction) => transaction.id === this.refundLicenseTx.id);
        if (current) {
          this.refundLicenseTx = current;
          if (current.status === 'refunding') this.refundLicenseTo = current.refund_to || this.refundLicenseTo;
        }
        this.refundLicenseLoading = false;
        this.update();
      }
    },

    // Refund state of a reload row, from the refunded total the API reports.
    reloadRemaining(t) {
      return Math.max(0, (t.amount || 0) - (t.refunded || 0));
    },

    reloadRefundState(t) {
      if ((t.type !== 'reload' && t.type !== 'subscription') || !(t.refunded > 0.004)) return '';
      return this.reloadRemaining(t) <= 0.004 ? 'refunded' : 'partially_refunded';
    },

    isFullyRefunded(t) {
      return this.reloadRefundState(t) === 'refunded';
    },

    refundReload(tx) {
      this.refundReloadTx = tx;
      this.refundAmount = '';
      this.refundLoading = false;
      this.querySelector('.refund-dialog').showModal();
    },

    closeRefundDialog() {
      this.querySelector('.refund-dialog').close();
    },

    async confirmRefund() {
      if (this.refundLoading) return;
      this.refundLoading = true;
      this.update();
      try {
        const body = { transaction_id: this.refundReloadTx.id };
        const amount = parseFloat(this.refundAmount);
        const remaining = this.reloadRemaining(this.refundReloadTx);
        if (amount > 0 && amount > remaining + 0.004) {
          this.showToast('Amount exceeds the remaining refundable ' + this.formatAmount(remaining), 'danger');
          return;
        }
        if (amount > 0) body.amount = amount;
        const response = await this.apiFetch(`${this.apiUrl('admin/refund-reload')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (response.ok) {
          this.showToast(data.message);
          this.closeRefundDialog();
          await this.loadAdminReloads();
        } else {
          this.showToast(data, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      } finally {
        this.refundLoading = false;
      }
    },

    async loadUsers() {
      try {
        if (await this.fetchList('users', `${env.apiUrl}admin/users`)) {
          this.usersLoaded = true;
        }
      } catch (e) {}
    },

    async loadSecurityEvents() {
      this.tableState('securityEvents').role ??= 'all';
      try {
        if (await this.fetchList('securityEvents', `${env.apiUrl}admin/security_events`)) {
          this.securityEventsLoaded = true;
        }
      } catch (e) {}
    },

    // Security log role filter: 'all' | 'admin' | 'user' (admin = events of
    // accounts currently flagged is_admin; user = everyone else, including
    // anonymous attempts and deleted accounts).
    setSecurityRole(role) {
      const st = this.tableState('securityEvents');
      if (st.role === role) return;
      st.role = role;
      st.page = 1;
      this._refetch('securityEvents');
      this._syncUrl();
    },

    securityEmptyTitle() {
      const role = this.tableState('securityEvents').role;
      if (role === 'admin') return 'No admin events';
      if (role === 'user') return 'No user events';
      return 'No security events';
    },

    openUserDialog(user) {
      this.userForm = {
        id: user.id,
        email: user.email,
        name: user.name || '',
        display_name: user.display_name || '',
        is_active: !!user.is_active,
        is_admin: !!user.is_admin,
        is_root: !!user.is_root,
      };
      this.update();
      this.querySelector('.user-dialog').showModal();
    },

    closeUserDialog() {
      this.querySelector('.user-dialog').close();
    },

    async saveUser() {
      try {
        const response = await this.apiFetch(`${env.apiUrl}admin/user?id=${encodeURIComponent(this.userForm.id)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: this.userForm.name,
            display_name: this.userForm.display_name,
            is_active: this.userForm.is_active,
            is_admin: this.userForm.is_admin,
            // Only roots see the Root checkbox; everyone else omits the
            // field entirely (the server rejects non-root changes to it).
            ...(this.isRoot ? { is_root: this.userForm.is_root } : {}),
          }),
        });
        const msg = await response.json();
        if (response.ok) {
          this.closeUserDialog();
          this.showToast(msg);
          await this.loadUsers();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    async deleteUser(user) {
      const confirmed = await this.showConfirm({
        title: 'Delete User',
        message: `Delete user "${user.email}"? This is a soft delete — the account will be marked inactive.`,
        action: 'Delete',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${env.apiUrl}admin/user?id=${encodeURIComponent(user.id)}`, { method: 'DELETE' });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadUsers();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    async openUserPasskeys(user) {
      this.userPasskeysUser = user;
      this.userPasskeys = [];
      this.userPasskeysLoaded = false;
      this.ensureAaguids();
      this.update();
      this.querySelector('.user-passkeys-dialog').showModal();
      await this.loadUserPasskeys();
    },

    async loadUserPasskeys() {
      try {
        const response = await this.apiFetch(`${env.apiUrl}admin/user/credentials?user_id=${encodeURIComponent(this.userPasskeysUser.id)}`);
        if (response.ok) {
          const data = await response.json();
          this.userPasskeys = data || [];
          this.userPasskeysLoaded = true;
        }
      } catch (e) {}
    },

    async deleteUserPasskey(cred) {
      const confirmed = await this.showConfirm({
        title: 'Delete Passkey',
        message: `Delete this passkey for "${this.userPasskeysUser.email}"? They will no longer be able to use it to sign in.`,
        action: 'Delete',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(`${env.apiUrl}admin/user/credentials?user_id=${encodeURIComponent(this.userPasskeysUser.id)}&id=${encodeURIComponent(cred.id)}`, { method: 'DELETE' });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadUserPasskeys();
          // The passkey count in the users table just changed.
          await this.loadUsers();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    closeUserPasskeys() {
      this.querySelector('.user-passkeys-dialog').close();
    },

    // ---- Per-user preferences (admin) ----

    async openUserPrefs(user) {
      this.userPrefsUser = user;
      this.userPrefs = [];
      this.userPrefsLoaded = false;
      this.update();
      this.querySelector('.user-prefs-dialog').showModal();
      await this.loadUserPrefs();
    },

    async loadUserPrefs() {
      try {
        const response = await this.apiFetch(`${env.apiUrl}admin/user/preferences?user_id=${encodeURIComponent(this.userPrefsUser.id)}`);
        if (response.ok) {
          this.userPrefs = this._prefRows(await response.json());
          this.userPrefsLoaded = true;
        }
      } catch (e) {}
    },

    // Add/edit through the shared preference dialog, targeted at this user.
    openUserPrefDialog(pref) {
      this.openPrefDialog(pref, true);
    },

    async deleteUserPref(pref) {
      const confirmed = await this.showConfirm({
        title: 'Delete Preference',
        message: `Delete the preference "${pref.name}" of "${this.userPrefsUser.email}"? Their apps will fall back to defaults.`,
        action: 'Delete',
        danger: true,
      });
      if (!confirmed) return;
      try {
        const response = await this.apiFetch(
          `${env.apiUrl}admin/user/preference?user_id=${encodeURIComponent(this.userPrefsUser.id)}&name=${encodeURIComponent(pref.name)}`,
          { method: 'DELETE' });
        const msg = await response.json();
        if (response.ok) {
          this.showToast(msg);
          await this.loadUserPrefs();
        } else {
          this.showToast(msg, 'danger');
        }
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },

    closeUserPrefs() {
      this.querySelector('.user-prefs-dialog').close();
    },
};
